import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Snackbar, Stack, TextField, Typography } from '@mui/material';
import DragAndDropColumns from '@/components/Base/DragAndDrop';
import { Add, ArrowBack, PostAdd } from '@mui/icons-material';
import AddCardResponsible from '@/features/responsible/AddCardResponsible.tsx';
import AddResponsible from '@/features/responsible/AddResponsible.tsx';
import { useListColumns } from '@/features/crm/api/listColumns';
import CreateColumnCrm from '@/features/crm/CreateColumnCrm';
import { TypesCRM } from '@/types/crm.ts';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import ListItems from '@/components/Base/ListItems.tsx';
import { groupBy } from 'lodash';
import { SerieModel } from '@/features/list-serie/types';
import useLoadingActions from '@/hooks/useLoadingActions.tsx';
import { TitlePages } from '@/components/Base/TitlePage';
import { ResponsibleCardModel } from '@/features/crm/types';
import { useNavigate } from 'react-router-dom';

type CrmPageProps = {
  title: string;
  type: TypesCRM;
};

export const CrmPage: React.FC<CrmPageProps> = ({ title, type }) => {
  const { setIsLoading } = useLoadingActions();

  const { data: columns = [], isFetched } = useListColumns(type);

  const [series, setSeries] = useState([]);
  const [serieId, setSerieId] = useState<string | number>('all');
  const [turmas, setTurmas] = useState([]);
  const [turmaId, setTurmaId] = useState<string | number>('all');

  const [turnos, setTurnos] = useState([]);
  const [turnoId, setTurnoId] = useState<string | number>('all');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [totalVagas, setTotalVagas] = useState<number>(0);
  const [nome, setNome] = useState<string>('');

  useEffect(() => {
    setIsLoading(true, 'Carregando...');
  }, [setIsLoading]);

  const {
    filters,
    openResponsible,
    openCardResponsible,
    setStep,
    toggleResponsible,
    toggleCardResponsible,
    setColumns,
    setType,
    setFilters,
  } = useModalStore((state) => state);
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (type) {
      setIsLoading(true, 'Carregando...');
      setType(type);
      setActiveFilter(null);
      setFilters({
        serieId: 'all',
        turmaId: 'all',
        turnoId: 'all',
      });
      setSerieId('all');
      setTurmaId('all');
      setTurmaId('all');
      setTotalVagas(0);
    }
  }, [type]);

  useEffect(() => {
    if (isFetched) {
      setStep(columns[0]);
      setColumns([...columns]);

      const { serieId = 'all', turmaId = 'all', turnoId = 'all' } = filters;

      let flattened = [];
      columns.forEach((column) => {
        // Verifica se column.cards existe antes de iterar
        if (column.cards && Array.isArray(column.cards)) {
          column.cards.forEach((card) => {
            flattened.push(card);
          });
        }
      });

      const filtered = flattened.filter((item) => {
        const responsavel = item?.responsavel as ResponsibleCardModel;
        const serie = (responsavel?.serie || {
          turma: {}, turno: {}
        }) as SerieModel;
        const { turma, turno } = serie;

        return (
          (serie.id === serieId || serieId === 'all') &&
          (turma.id === turmaId || turmaId === 'all') &&
          (turno.id === turnoId || turnoId === 'all')
        );
      });

      const paineis = groupBy(filtered, 'painel_ocupacao.id');

      setTotalVagas(Number(Object.values(paineis).reduce((total, item) => total + item[0]?.painel_ocupacao?.livre, 0)));

      if (activeFilter !== 'serie' || serieId === 'all') {
        const seriesGroup = groupBy(filtered, 'responsavel.serie.serie_nome');
        setSeries(
          Object.keys(seriesGroup)
            .filter((key) => {
              const responsavel = seriesGroup[key][0]?.responsavel as ResponsibleCardModel;
              const serie = (responsavel?.serie || {
                turma: {}, turno: {}
              }) as SerieModel;
              const { turma, turno } = serie;

              return (
                (serie.id === serieId || serieId === 'all') &&
                (turma.id === turmaId || turmaId === 'all') &&
                (turno.id === turnoId || turnoId === 'all')
              );
            })
            .map((key) => {
              const item = seriesGroup[key][0]?.responsavel?.serie;
              return {
                id: item?.id,
                label: key,
              };
            }),
        );
      }

      if (activeFilter !== 'turma' || turmaId === 'all') {
        const turmasGroup = groupBy(filtered, 'responsavel.serie.turma.nome');
        setTurmas(
          Object.keys(turmasGroup)
            .filter((key) => {
              const responsavel = turmasGroup[key][0]?.responsavel as ResponsibleCardModel;
              const serie = (responsavel?.serie || {
                turma: {}, turno: {}
              }) as SerieModel;
              const { turma, turno } = serie;

              return (
                (serie.id === serieId || serieId === 'all') &&
                (turma.id === turmaId || turmaId === 'all') &&
                (turno.id === turnoId || turnoId === 'all')
              );
            })
            .map((key) => {
              const item = turmasGroup[key][0]?.responsavel?.serie?.turma;
              return {
                id: item?.id,
                label: key,
              };
            }),
        );
      }

      if (activeFilter !== 'turno' || turnoId === 'all') {
        const turnosGroup = groupBy(filtered, 'responsavel.serie.turno.nome');
        setTurnos(
          Object.keys(turnosGroup)
            .filter((key) => {
              const responsavel = turnosGroup[key][0]?.responsavel;
              const serie = (responsavel?.serie || {
                turma: {}, turno: {}
              }) as SerieModel;
              const { turma, turno } = serie;

              return (
                (serie.id === serieId || serieId === 'all') &&
                (turma.id === turmaId || turmaId === 'all') &&
                (turno.id === turnoId || turnoId === 'all')
              );
            })
            .map((key) => {
              const item = turnosGroup[key][0]?.responsavel?.serie?.turno;
              return {
                id: item?.id,
                label: key,
              };
            }),
        );
      }

      setIsLoading(false, '');
    }
  }, [isFetched, columns, filters]);

  const handleClose = () => {
    toggleResponsible(false);
  };

  const handleCloseCard = () => {
    toggleCardResponsible(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const onApplyFilter = () => {
    setFilters({
      serieId,
      turmaId,
      turnoId,
    });
  };

  const navigate = useNavigate();

  return (
    <>
      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>

        <Stack flexDirection={'row'} alignItems={'center'} gap={4}>
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <TitlePages title={title} />
        </Stack>

        {title === 'CRM Matrícula' && (
          <Button
            sx={{
              p: '0px, 16px, 0px, 16px',
              borderRadius: '24px',
              fontSize: '14px',
              height: '37px',
              color: '#fff !important',
              weight: '600',
              background: '#15AB51',
              fontWeight: '700',
              textTransform: 'capitalize',
            }}
            variant="contained"
            onClick={() => toggleResponsible(true)}
          >
            <Add />
            Cadastrar responsável financeiro
          </Button>
        )}
      </Stack>

      <Box sx={{ backgroundColor: 'transparent' }}>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'end',
            p: '15px',
            '.MuiInputBase-input': { padding: '5px', color: '#000' },
            '.MuiButtonBase-root': { color: '#000' },
            '.MuiTypography-root': { color: '#000' },
            background: '#FFF',
            borderRadius: '8px'
          }}
        >
          <Box maxWidth={'220px'} width={'100%'}>
            <Typography mb={'7px'}>Nome</Typography>
            <TextField
              name={'nome'}
              value={nome}
              onChange={(e) => {
                setNome(e.target.value);
              }}
              sx={{
                border: "1px solid black",
                borderRadius: '6px'
              }}
            />
          </Box>

          <Box maxWidth={'250px'} width={'100%'}>
            <ListItems
              name={'serie_id'}
              options={series}
              label={'Série*'}
              value={serieId}
              onChange={(e) => {
                setSerieId(e.target.value);
                setActiveFilter('serie');
              }}
            />
          </Box>

          <Box maxWidth={'250px'} width={'100%'}>
            <ListItems
              name={'turma_id'}
              options={turmas}
              label={'Turma*'}
              value={turmaId}
              onChange={(e) => {
                setTurmaId(e.target.value);
                setActiveFilter('turma');
              }}
            />
          </Box>

          <Box maxWidth={'250px'} width={'100%'}>
            <ListItems
              name={'turno_id'}
              options={turnos}
              label={'Turno*'}
              value={turnoId}
              onChange={(e) => {
                setTurnoId(e.target.value);
                setActiveFilter('turno');
              }}
            />
          </Box>

          <Button
            sx={{
              maxWidth: '200px',
              p: '0 5px',
              borderRadius: '24px',
              fontSize: '14px',
              height: '37px',
              color: '#fff !important',
              width: '188px',
              weight: '600',
              background: '#15AB51',
              textTransform: 'capitalize'
            }}
            variant="contained"
            onClick={onApplyFilter}
          >
            Filtrar
          </Button>

          <Box
            sx={{
              border: '2px solid red',
              borderRadius: '6px',
              backgroundColor: 'rgba(252, 228, 212, 1)',
              p: '16px',
              minWidth: '200px',
              color: ' rgba(52, 52, 52, 1)',
              fontWeight: 600,
            }}
          >
            Vagas: {totalVagas}
          </Box>
        </Box>

        <Stack flexDirection={'row'}>
          {!!columns.length && (
            <div>
              <DragAndDropColumns type={type} columns={columns} setOpenModal={setOpenModal} filterName={nome} />
            </div>
          )}
          <Stack alignItems={'flex-start'} display={'none'}>
            <Button
              variant="text"
              startIcon={<Add sx={{height: '16px'}}/>}
              endIcon={<PostAdd sx={{height: '16px'}}/>}
              sx={{color: '#646570', fontWeight: 500, fontSize: '14px', textTransform: 'capitalize', minWidth: '200px', mt: '20px'}}
              /* onClick={() => {}} */
            >
              Adicionar um cartão
            </Button>
          </Stack>
        </Stack>

        {openResponsible && <AddResponsible handleClose={handleClose} />}

        {openCardResponsible && <AddCardResponsible handleClose={handleCloseCard} type={type} />}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity as 'success' | 'error'}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {openModal && <CreateColumnCrm handleClose={() => setOpenModal(false)} type={type} />}
    </>
  );
};
